import React, { useEffect, useState } from 'react';
import { X, Save, Tag as TagIcon } from 'lucide-react';
import { CategoryResponseDTO, CategoryRequestDTO } from '../../types/category';
import { categoryService } from '../../services/categoryService';
import { alertService } from '../../services/alertService';

interface CategoryModalProps {
  category?: CategoryResponseDTO | null;
  onClose: () => void;
}

interface CategoryFormData {
  nome: string;
  descricao: string;
  categoriaPaiId: string;
  ativo: boolean;
  observacoes: string;
}

const CategoryModal: React.FC<CategoryModalProps> = ({ category, onClose }) => {
  const [formData, setFormData] = useState<CategoryFormData>({
    nome: '',
    descricao: '',
    categoriaPaiId: '',
    ativo: true,
    observacoes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        nome: category.nome || '',
        descricao: category.descricao || '',
        categoriaPaiId: category.categoriaPaiId || '',
        ativo: category.ativo,
        observacoes: category.observacoes || ''
      });
    } else {
      setFormData({
        nome: '',
        descricao: '',
        categoriaPaiId: '',
        ativo: true,
        observacoes: ''
      });
    }
    setErrors({});
  }, [category]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload: CategoryRequestDTO = {
        nome: formData.nome,
        descricao: formData.descricao || undefined,
        categoriaPaiId: formData.categoriaPaiId || undefined,
        ativo: formData.ativo,
        observacoes: formData.observacoes || undefined,
      };

      if (category) {
        await categoryService.update(category.id, payload);
        alertService.success('Categoria atualizada com sucesso!');
      } else {
        await categoryService.create(payload);
        alertService.success('Categoria criada com sucesso!');
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      alertService.error('Erro ao salvar categoria');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <TagIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {category ? 'Editar Categoria' : 'Nova Categoria'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Dados Básicos */}
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Dados da Categoria</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.nome ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Digite o nome da categoria"
                    />
                    {errors.nome && <p className="mt-1 text-sm text-red-600">{errors.nome}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoria Pai (ID)</label>
                    <input
                      type="text"
                      name="categoriaPaiId"
                      value={formData.categoriaPaiId}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent border-gray-300"
                      placeholder="Informe o ID da categoria pai (opcional)"
                    />
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent border-gray-300"
                  placeholder="Descreva a categoria (opcional)"
                />
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent border-gray-300"
                  placeholder="Observações adicionais (opcional)"
                />
              </div>

              {/* Ativo */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleChange}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Categoria ativa</span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Salvando...' : (category ? 'Atualizar' : 'Criar')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;